import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Cookie, X } from 'lucide-react';

const STORAGE_KEY = 'bt_analytics_consent';

/**
 * Consent gate for PostHog analytics and session replay.
 *
 * PostHog is deliberately NOT initialised on page load — see the bootstrap in
 * public/index.html. Calling posthog.init() is what loads array.js, POSTs to
 * /flags and writes the ph_* cookie, so it must not happen before the visitor
 * accepts. Until then there is no PostHog cookie, no localStorage entry and no
 * network request, which is what the DPDP Act / GDPR / ePrivacy actually
 * require of a non-essential cookie.
 */
export const CookieConsent = () => {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let stored = null;
    try {
      stored = window.localStorage.getItem(STORAGE_KEY);
    } catch {
      // Private mode / storage blocked — show the banner but don't persist.
    }

    if (stored === 'granted') {
      // index.html normally handles this; call again in case storage was
      // written after that script ran. __initPostHog is idempotent.
      window.__initPostHog?.();
    } else if (stored !== 'denied') {
      setVisible(true);
    }
  }, []);

  const persist = (value) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, value);
    } catch {
      // Ignore — the banner will simply reappear next visit.
    }
  };

  const accept = () => {
    persist('granted');
    window.__initPostHog?.();
    setVisible(false);
  };

  const decline = () => {
    persist('denied');
    // Nothing to tear down unless consent was previously granted this session.
    window.posthog?.opt_out_capturing?.();
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ type: 'spring', stiffness: 260, damping: 26 }}
          role="dialog"
          aria-live="polite"
          aria-label="Cookie consent"
          className="fixed bottom-4 left-4 right-4 sm:right-auto sm:max-w-md z-[60]"
        >
          <div className="bg-slate-950/95 border border-white/10 rounded-2xl p-5 backdrop-blur-xl shadow-2xl shadow-black/60">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-violet-600/10 border border-violet-500/30 flex items-center justify-center flex-shrink-0">
                <Cookie size={16} className="text-violet-300" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-bold text-white mb-1.5">We'd like to use analytics</h3>
                <p className="text-xs text-slate-400 leading-relaxed">
                  We use PostHog to understand how this site is used, which includes recording
                  anonymised session replays. Anything you type into a form is masked. Nothing is
                  captured unless you accept.{' '}
                  <button
                    onClick={() => navigate('/privacy')}
                    className="text-violet-300 hover:text-violet-200 underline underline-offset-2"
                  >
                    Privacy Policy
                  </button>
                </p>

                <div className="flex flex-wrap gap-2 mt-4">
                  <button
                    onClick={accept}
                    className="px-4 py-2 rounded-xl bg-gradient-to-r from-violet-600 to-cyan-600 text-xs font-bold shadow-lg shadow-violet-500/20"
                  >
                    Accept
                  </button>
                  <button
                    onClick={decline}
                    className="px-4 py-2 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 text-xs font-semibold transition-all"
                  >
                    Decline
                  </button>
                </div>
              </div>
              <button
                onClick={decline}
                aria-label="Decline analytics and close"
                className="text-slate-500 hover:text-slate-300 flex-shrink-0 transition-colors"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
